import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, HTMLAttributes } from 'react';
import { motion, useAnimation, Variants, Transition } from 'framer-motion';

export interface UserRoundCheckIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

export interface UserRoundCheckIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered?: boolean;
}

const CHECK_VARIANTS: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      pathLength: { duration: 0.4, ease: "easeInOut" },
      opacity: { duration: 0.4, ease: "easeInOut" },
    },
  },
};

const UserRoundCheckIcon = forwardRef<
  UserRoundCheckIconHandle,
  UserRoundCheckIconProps
>(({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
  const controls = useAnimation();
  const isControlledRef = useRef(false);

  useImperativeHandle(ref, () => {
    isControlledRef.current = true;

    return {
      startAnimation: () => controls.start("animate"),
      stopAnimation: () => controls.start("normal"),
    };
  });

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isControlledRef.current) {
        onMouseEnter?.(e);
      } else {
        controls.start("animate");
      }
    },
    [controls, onMouseEnter]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isControlledRef.current) {
        onMouseLeave?.(e);
      } else {
        controls.start("normal");
      }
    },
    [controls, onMouseLeave]
  );

    useEffect(() => {
      if (isHovered !== undefined) {
        if (isHovered) {
          if (ref && 'current' in ref && ref.current && typeof (ref.current as any).startAnimation === 'function') {
            (ref.current as any).startAnimation();
          } else {
            handleMouseEnter({} as React.MouseEvent<HTMLDivElement>);
          }
        } else {
          if (ref && 'current' in ref && ref.current && typeof (ref.current as any).stopAnimation === 'function') {
            (ref.current as any).stopAnimation();
          } else {
            handleMouseLeave({} as React.MouseEvent<HTMLDivElement>);
          }
        }
      }
    }, [isHovered, handleMouseEnter, handleMouseLeave, ref]);


  return (
    <div
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <svg
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M2 21a8 8 0 0 1 13.292-6" />
        <circle cx="10" cy="8" r="5" />
        <motion.path
          animate={controls}
          d="m16 19 2 2 4-4"
          initial="normal"
          style={{ transformOrigin: "center" }}
          variants={CHECK_VARIANTS}
        />
      </svg>
    </div>
  );
});

UserRoundCheckIcon.displayName = "UserRoundCheckIcon";

export { UserRoundCheckIcon };
